import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import DatePicker from 'react-datepicker';

import { buscarSucursales } from '../../actions/sucursales';
import { buscarEventos } from '../../actions/eventos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import {
    generarReporteVentasProducto,
    generarReporteVentasEmpleado,
    generarReporteGastos,
    generarReporteNomina,
    generarReporteSucursales,
    generarReporteEventos
} from '../../actions/reportes';

import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const styles_select = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({ ...provided }),
    control: (provided) => ({ ...provided, paddingTop: '5px', paddingBottom: '5px' }),
    valueContainer: (provided) => ({ ...provided, overflow: 'visible', marginTop: '10px' }),
    placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
        transition: 'top 0.1s, font-size 0.1s',
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

/* ── Tarjeta de reporte con filtros propios ── */
const ReporteCard = ({ titulo, descripcion, icon, color, onGenerar }) => {

    const sucursalesListado = useSelector(state => state.sucursales.listado);
    const eventosListado    = useSelector(state => state.eventos.listado);

    const [sucursalData, setSucursalData] = useState(null);
    const [eventoData,   setEventoData]   = useState(null);
    const [fechaInicio,  setFechaInicio]  = useState(null);
    const [fechaFin,     setFechaFin]     = useState(null);

    const handleGenerar = () => {
        onGenerar({
            sucursal:     sucursalData ? sucursalData.value : null,
            evento:       eventoData   ? eventoData.value   : null,
            fecha_inicio: fechaInicio  ? new Date(new Date(fechaInicio).setHours(0, 0, 0, 0))    : null,
            fecha_fin:    fechaFin     ? new Date(new Date(fechaFin).setHours(23, 59, 59, 999))  : null
        });
    };

    const sucursalesOpciones = sucursalesListado.map(s => ({ value: s._id, label: s.nombre }));
    const eventosOpciones    = eventosListado.map(e => ({ value: e._id, label: e.nombre }));

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                    <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0`}
                        style={{ width: 36, height: 36 }}>
                        <i className={`fas ${icon} text-${color} small`}></i>
                    </div>
                    <div>
                        <h6 className="mb-0 fw-semibold">{titulo}</h6>
                        <small className="text-muted">{descripcion}</small>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row g-3 align-items-end">

                    <div className="col-md-3 form-group">
                        <div className="input-group h-100">
                            <Select
                                className="select-group w-100"
                                classNamePrefix="react-select"
                                placeholder="Sucursal"
                                options={sucursalesOpciones}
                                value={sucursalData}
                                onChange={option => setSucursalData(option)}
                                menuPortalTarget={document.body}
                                components={{ ValueContainer: CustomValueContainer }}
                                styles={{ ...styles_select }}
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="col-md-3 form-group">
                        <div className="input-group h-100">
                            <Select
                                className="select-group w-100"
                                classNamePrefix="react-select"
                                placeholder="Evento"
                                options={eventosOpciones}
                                value={eventoData}
                                onChange={option => setEventoData(option)}
                                menuPortalTarget={document.body}
                                components={{ ValueContainer: CustomValueContainer }}
                                styles={{ ...styles_select }}
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="col-md-2">
                        <div className="input-group">
                            <div className="form-floating">
                                <DatePicker
                                    id={`fecha_inicio_${titulo}`}
                                    className="form-control text-center input-datepicker"
                                    selected={fechaInicio}
                                    onChange={date => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    autoComplete="off"
                                />
                                <label className="label-datepicker" htmlFor={`fecha_inicio_${titulo}`}>Fecha inicio</label>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-2">
                        <div className="input-group">
                            <div className="form-floating">
                                <DatePicker
                                    id={`fecha_fin_${titulo}`}
                                    className="form-control text-center input-datepicker"
                                    selected={fechaFin}
                                    onChange={date => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    autoComplete="off"
                                    minDate={fechaInicio}
                                />
                                <label className="label-datepicker" htmlFor={`fecha_fin_${titulo}`}>Fecha fin</label>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-2 d-flex justify-content-end">
                        <button className={`btn btn-${color} w-100`} onClick={handleGenerar}>
                            <i className="fas fa-file-excel me-2"></i>
                            Generar
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

/* ── Tarjeta de resumen con multi-select ── */
const ReporteCardResumen = ({ titulo, descripcion, icon, color, tipo, onGenerar }) => {

    const sucursalesListado = useSelector(state => state.sucursales.listado);
    const eventosListado    = useSelector(state => state.eventos.listado);

    const [seleccion,   setSeleccion]   = useState([]);
    const [fechaInicio, setFechaInicio] = useState(null);
    const [fechaFin,    setFechaFin]    = useState(null);

    const opciones = tipo === 'sucursales'
        ? sucursalesListado.map(s => ({ value: s._id, label: s.nombre }))
        : eventosListado.map(e => ({ value: e._id, label: e.nombre }));

    const placeholder = tipo === 'sucursales' ? 'Sucursales' : 'Eventos';

    const handleGenerar = () => {
        const ids = seleccion.map(o => o.value);
        onGenerar({
            [tipo]:       ids.length > 0 ? ids : null,
            fecha_inicio: fechaInicio ? new Date(new Date(fechaInicio).setHours(0, 0, 0, 0))   : null,
            fecha_fin:    fechaFin    ? new Date(new Date(fechaFin).setHours(23, 59, 59, 999)) : null
        });
    };

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-2">
                    <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0`}
                        style={{ width: 36, height: 36 }}>
                        <i className={`fas ${icon} text-${color} small`}></i>
                    </div>
                    <div>
                        <h6 className="mb-0 fw-semibold">{titulo}</h6>
                        <small className="text-muted">{descripcion}</small>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row g-3 align-items-end">

                    <div className="col-md-6 form-group">
                        <div className="input-group h-100">
                            <Select
                                className="select-group w-100"
                                classNamePrefix="react-select"
                                placeholder={placeholder}
                                options={opciones}
                                value={seleccion}
                                onChange={opts => setSeleccion(opts || [])}
                                menuPortalTarget={document.body}
                                components={{ ValueContainer: CustomValueContainer }}
                                styles={{ ...styles_select }}
                                isMulti
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="col-md-2">
                        <div className="input-group">
                            <div className="form-floating">
                                <DatePicker
                                    id={`fecha_inicio_${titulo}`}
                                    className="form-control text-center input-datepicker"
                                    selected={fechaInicio}
                                    onChange={date => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    autoComplete="off"
                                />
                                <label className="label-datepicker" htmlFor={`fecha_inicio_${titulo}`}>Fecha inicio</label>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-2">
                        <div className="input-group">
                            <div className="form-floating">
                                <DatePicker
                                    id={`fecha_fin_${titulo}`}
                                    className="form-control text-center input-datepicker"
                                    selected={fechaFin}
                                    onChange={date => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    isClearable
                                    autoComplete="off"
                                    minDate={fechaInicio}
                                />
                                <label className="label-datepicker" htmlFor={`fecha_fin_${titulo}`}>Fecha fin</label>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-2 d-flex justify-content-end">
                        <button className={`btn btn-${color} w-100`} onClick={handleGenerar}>
                            <i className="fas fa-file-excel me-2"></i>
                            Generar
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

/* ── Vista principal ── */
const Reportes = () => {

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(buscarSucursales());
        dispatch(buscarEventos(false));
        dispatch(editarBreadcrumb([
            { nombre: 'Reportes', path: '/reportes', activo: true }
        ]));
    }, []);

    const reportes = [
        {
            titulo:      'Ventas por producto',
            descripcion: 'Detalle de los productos contemplados en ventas fijas y ambulantes',
            icon:        'fa-ice-cream',
            color:       'success',
            onGenerar:   (filtros) => dispatch(generarReporteVentasProducto(filtros))
        },
        {
            titulo:      'Ventas por empleado',
            descripcion: 'Detalle de los empleados contemplados en ventas fijas y ambulantes',
            icon:        'fa-user-tag',
            color:       'info',
            onGenerar:   (filtros) => dispatch(generarReporteVentasEmpleado(filtros))
        },
        {
            titulo:      'Gastos',
            descripcion: 'Gastos registrados desde el PDV y el administrador, incluyendo status de pago y reembolso',
            icon:        'fa-receipt',
            color:       'danger',
            onGenerar:   (filtros) => dispatch(generarReporteGastos(filtros))
        },
        {
            titulo:      'Nómina',
            descripcion: 'Información agrupada de las nóminas generadas',
            icon:        'fa-file-invoice-dollar',
            color:       'warning',
            onGenerar:   (filtros) => dispatch(generarReporteNomina(filtros))
        }
    ];

    const reportesResumen = [
        {
            titulo:      'Resúmen de sucursales',
            descripcion: 'Resúmen de ventas, gastos y cortes agrupados por sucursal',
            icon:        'fa-store',
            color:       'info',
            tipo:        'sucursales',
            onGenerar:   (filtros) => dispatch(generarReporteSucursales(filtros))
        },
        {
            titulo:      'Resúmen de eventos',
            descripcion: 'Resúmen de ventas, gastos y cortes agrupados por evento',
            icon:        'fa-calendar-alt',
            color:       'secondary',
            tipo:        'eventos',
            onGenerar:   (filtros) => dispatch(generarReporteEventos(filtros))
        }
    ];

    return (
        <div className="row mt-5">
            <div className="col-12 mb-4">
                <h4 className="mb-0">Reportes</h4>
            </div>

            {reportes.map(r => (
                <div className="col-12 mb-3" key={r.titulo}>
                    <ReporteCard {...r} />
                </div>
            ))}

            {reportesResumen.map(r => (
                <div className="col-12 mb-3" key={r.titulo}>
                    <ReporteCardResumen {...r} />
                </div>
            ))}
        </div>
    );
};

export default Reportes;
